import React, { useEffect, useRef, useState } from 'react';
import gwsLogo from '../img/gws-logo.png';
import gwsFullLogo from '../img/gws-full-logo.png';

/*  —  —  —  —  —  Design tokens  —  —  —  —  —  */
const INK = '#101311';
const GREEN = '#1a441f';
const GREEN_DEEP = '#123316';
const COPPER = '#e4a483';
const PAPER = '#f7f5f1';
const SLATE = '#587c8d';

const TOTAL = 8;

/* Pitch deck events come from the GWS app: every published event an admin has
   given a Pitch Deck Category. The key is the public (publishable) one. */
const SUPABASE_URL = 'https://yzzynxgixflgjzvotiae.supabase.co';
const SUPABASE_PUBLISHABLE_KEY = 'sb_publishable_Uo41d2KwkygT1-eHklyKsA_YQHjXf-H';

const formatDeckDate = (row) => {
  if (row.is_tba || !row.start_date) return 'TBA';
  try {
    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      timeZone: row.timezone || 'Europe/Istanbul',
    }).format(new Date(row.start_date));
  } catch {
    return '';
  }
};

/* Section number shown per slide  —  the cover carries none */
const SECTION_NO = [null, 1, 2, 3, 4, 5, 6, 7];
const SECTION_COUNT = 7;

/* Membership has two layouts. false ships the winding path, true brings back the
   original five-column list. Both are kept below; this is the only switch. */
const LEGACY_MEMBERSHIP = false;

/* Grain texture as inline SVG */
const GRAIN =
  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.5'/%3E%3C/svg%3E\")";

const Eyebrow = ({ children, light, d = '0s' }) => (
  <div
    className="rv flex items-center gap-4"
    style={{ '--d': d }}
  >
    <span
      className="block h-px w-10"
      style={{ backgroundColor: light ? COPPER : GREEN, opacity: 0.6 }}
    />
    <span
      className="text-[11px] tracking-[0.35em] uppercase font-medium"
      style={{ color: light ? COPPER : GREEN, opacity: 0.75 }}
    >
      {children}
    </span>
  </div>
);

/* `className` is for nudging one ghost off the shared top offset  —  margin still
   applies to an absolutely positioned box, so it reads as an extra offset on top of
   `top` and can be made responsive the way an inline style can't. */
const Ghost = ({ n, text, light, small, centered, size, className = '' }) => (
  <span
    aria-hidden
    /* `small` only kicks in from lg up, where the slide splits in two; stacked on
       mobile each half is full-width, so the ghost matches every other slide's 11vw */
    className={`gws-ghost gws-display absolute select-none pointer-events-none leading-none ${small ? 'text-[11vw] lg:text-[5.5vw]' : 'text-[11vw]'} ${centered ? 'left-0 right-0 text-center' : 'left-4 lg:left-8'} ${className}`}
    style={{
      /* 48px at ≥1400px (untouched desktop), then GROWS as the screen narrows  —
         +0.04px per px below 1400, capped at 96px  —  so it never hugs the top */
      top: 'clamp(3rem, calc(3rem + (1400px - 100vw) * 0.04), 6rem)',
      color: light ? 'rgba(228,164,131,0.15)' : 'rgba(26,68,31,0.10)',
      WebkitTextStroke: 'unset',
      fontSize: size,
    }}
  >
    {text || n}
  </span>
);

/* The seam between the two halves, read as a zip rather than a butt edge: short thick
   fingers anchored on the divider, reaching into the opposite half and meshing. A finger
   crossing onto the dark half is paper-white, one crossing onto the pale half is the
   brand green  —  full strength both, no transparency: over these grounds any opacity
   drags both colours to the same washed sage and the two sides stop reading as two
   sides. Fingers are packed tight (gap well under thickness) so neighbours from
   opposite halves visibly interleave; all of them share one length, a uniform zip. */
const SEAM_LENGTH = 26;   /* every finger, both sides */
const SEAM_THICKNESS = 14;
const SEAM_GAP = 8;
const SEAM_COUNT = 60; /* generous  —  the container clips whatever runs past the fold */

const Seam = () => (
  <div
    aria-hidden
    className="pointer-events-none absolute inset-y-0 left-1/2 z-20 hidden w-[160px] -translate-x-1/2 flex-col items-stretch justify-center overflow-hidden lg:flex"
    style={{ gap: SEAM_GAP }}
  >
    {Array.from({ length: SEAM_COUNT }).map((_, i) => {
      const reachesRight = i % 2 === 0;
      return (
        <div key={i} className="relative w-full shrink-0" style={{ height: SEAM_THICKNESS }}>
          <span
            className="absolute inset-y-0"
            style={{
              width: SEAM_LENGTH,
              /* Anchored on the centre line, rounded only on the reaching end */
              [reachesRight ? 'left' : 'right']: '50%',
              background: reachesRight ? PAPER : GREEN,
              borderRadius: reachesRight ? '0 999px 999px 0' : '999px 0 0 999px',
            }}
          />
        </div>
      );
    })}
  </div>
);

/* The same zip below lg, where the two halves stack and the seam runs horizontally.
   It mounts inside the pale half and straddles that half's bottom edge, which is the
   colour boundary  —  the fold has no fixed offset once the halves stack, so it can't
   be placed off the outer grid the way the vertical seam is. Reaching down lands on
   the dark half, so those fingers are paper; reaching up lands on the pale half, green. */
const SeamHorizontal = () => (
  <div
    aria-hidden
    /* -bottom-20 straddles the fold on half the box height rather than translate-y-1/2:
       a transform would put the strip on its own composited layer, and at the fold's
       fractional y that layer rasterises half a pixel off, greying the whole seam. */
    className="pointer-events-none absolute inset-x-0 -bottom-20 z-20 flex h-40 items-stretch justify-center overflow-hidden lg:hidden"
    style={{ gap: SEAM_GAP }}
  >
    {Array.from({ length: SEAM_COUNT }).map((_, i) => {
      const reachesDown = i % 2 === 0;
      return (
        <div key={i} className="relative h-full shrink-0" style={{ width: SEAM_THICKNESS }}>
          <span
            className="absolute inset-x-0"
            style={{
              /* each finger crosses the fold by a pixel, so its own colour  —  not a
                 half-covered blend of the two grounds  —  paints that row */
              height: SEAM_LENGTH + 1,
              [reachesDown ? 'top' : 'bottom']: 'calc(50% - 1px)',
              background: reachesDown ? PAPER : GREEN,
              borderRadius: reachesDown ? '0 0 999px 999px' : '999px 999px 0 0',
            }}
          />
        </div>
      );
    })}
  </div>
);


/* One event, in the shape the app shows it: the artwork carries the card and a flat
   overlay drops it back far enough for the type to sit on top. */
const EventCard = ({ ev }) => (
  <div
    className="relative h-full w-full overflow-hidden rounded-xl"
    style={{
      minHeight: '230px',
      /* placeholder events ship without art  —  fall back to flat green */
      background: ev.img ? `url("${ev.img}") center/cover` : GREEN_DEEP,
    }}
  >
    {/* Dark overlay  —  the app's rgba(50, 71, 80, 0.70) */}
    <div className="absolute inset-0" style={{ background: 'rgba(50, 71, 80, 0.70)' }} />

    <div className="relative flex flex-col gap-2 p-4">
      <h4
        className="gws-display text-[17px] leading-tight text-white"
        style={{ fontVariationSettings: "'SOFT' 100, 'WONK' 0, 'opsz' 144, 'wght' 600" }}
      >
        {ev.title}
      </h4>

      <p
        className="overflow-hidden text-[13px] font-light leading-snug text-white/80"
        style={{ display: '-webkit-box', WebkitLineClamp: 4, WebkitBoxOrient: 'vertical' }}
      >
        {ev.excerpt}
      </p>

      <div className="flex text-[11px] leading-tight">
        <span className="font-semibold shrink-0" style={{ color: '#ced9cf' }}>Host:</span>
        <span className="ml-1 font-medium text-white">{ev.host}</span>
      </div>
      <div className="flex text-[11px] leading-tight">
        <span className="font-semibold shrink-0" style={{ color: '#ced9cf' }}>Date:</span>
        <span className="ml-1 font-medium text-white">{ev.date}</span>
      </div>
      <div className="flex text-[11px] leading-tight">
        <span className="font-semibold shrink-0" style={{ color: '#ced9cf' }}>Location:</span>
        <span className="ml-1 font-medium text-white">{ev.location}</span>
      </div>
    </div>
  </div>
);

/* Draggable infinite marquee - auto-scrolls, draggable, loops forever */
const DraggableMarquee = ({ events, children }) => {
  const scrollRef = useRef(null);
  const animationRef = useRef(null);
  const isDragging = useRef(false);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    // Start in the middle copy so there's room to drag both ways
    const setWidth = () => container.scrollWidth / 3;
    container.scrollLeft = setWidth();

    // Auto-scroll animation. scrollLeft rounds to integers in most browsers, so a
    // fractional step added directly would truncate to zero movement  —  accumulate
    // the position in a float and assign it instead.
    let scrollSpeed = 0.4;
    let pos = container.scrollLeft;
    const animate = () => {
      if (container) {
        if (!isDragging.current) {
          pos += scrollSpeed;
          container.scrollLeft = pos;
        } else {
          pos = container.scrollLeft;
        }

        // Infinite loop both ways: keep the viewport inside the middle copy.
        // When wrapping mid-drag, shift the drag anchor too so there's no jump.
        const w = setWidth();
        if (container.scrollLeft >= w * 2) {
          container.scrollLeft -= w;
          pos -= w;
          scrollLeft -= w;
        } else if (container.scrollLeft <= 0) {
          container.scrollLeft += w;
          pos += w;
          scrollLeft += w;
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);

    // Drag support
    let startX;
    let scrollLeft;

    const handleMouseDown = (e) => {
      isDragging.current = true;
      container.style.cursor = 'grabbing';
      startX = e.pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    };

    const handleMouseMove = (e) => {
      if (!isDragging.current) return;
      e.preventDefault();
      const x = e.pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    const handleMouseUp = () => {
      isDragging.current = false;
      container.style.cursor = 'grab';
    };

    const handleTouchStart = (e) => {
      isDragging.current = true;
      startX = e.touches[0].pageX - container.offsetLeft;
      scrollLeft = container.scrollLeft;
    };

    const handleTouchMove = (e) => {
      if (!isDragging.current) return;
      const x = e.touches[0].pageX - container.offsetLeft;
      const walk = (x - startX) * 2;
      container.scrollLeft = scrollLeft - walk;
    };

    const handleTouchEnd = () => {
      isDragging.current = false;
    };

    container.addEventListener('mousedown', handleMouseDown);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseUp);
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd);

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (container) {
        container.removeEventListener('mousedown', handleMouseDown);
        container.removeEventListener('mousemove', handleMouseMove);
        container.removeEventListener('mouseup', handleMouseUp);
        container.removeEventListener('mouseleave', handleMouseUp);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
        container.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, []);

  return (
    <div
      ref={scrollRef}
      className={`${events.length >= 3 ? '' : 'lg:hidden'} overflow-x-auto scrollbar-hide`}
      style={{
        cursor: 'grab',
        userSelect: 'none',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
      }}
    >
      <div style={{ display: 'flex', gap: '0.75rem' }}>
        {children}
        {children}
        {children}
      </div>
    </div>
  );
};

const PitchDeck = () => {
  useEffect(() => {
    document.title = 'GWS  —  Pitch Deck';
  }, []);

  /* null until loaded; the built-in list below stays on screen until then and
     whenever the request fails */
  const [deckEvents, setDeckEvents] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${SUPABASE_URL}/rest/v1/rpc/get_pitch_deck_events`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        'Content-Type': 'application/json',
      },
      body: '{}',
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((rows) => {
        if (cancelled || !Array.isArray(rows)) return;
        setDeckEvents(
          rows.map((row) => ({
            category: row.pitch_deck_category,
            title: row.title,
            excerpt: row.excerpt || '',
            host: row.host || '',
            date: formatDeckDate(row),
            location: row.location || '',
            img: row.image_url || '',
          }))
        );
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /* Current member count from the app, already less the four test accounts.
     96 stays on screen until it loads and whenever the request fails. */
  const [memberCount, setMemberCount] = useState(96);

  useEffect(() => {
    let cancelled = false;
    fetch(`${SUPABASE_URL}/rest/v1/rpc/get_pitch_deck_member_count`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_PUBLISHABLE_KEY,
        'Content-Type': 'application/json',
      },
      body: '{}',
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(String(res.status)))))
      .then((count) => {
        if (!cancelled && Number.isInteger(count) && count > 0) setMemberCount(count);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  /*  —  —  —  —  —  Slides  —  —  —  —  —  */

  const slides = [
    /* 01  —  Cover */
    {
      bg: `radial-gradient(120% 120% at 80% 0%, ${GREEN} 0%, ${GREEN_DEEP} 55%, #0c2410 100%)`,
      content: (
        <div className="relative flex h-full w-full items-center justify-center px-8">
          <div className="rv" style={{ '--d': '0.2s' }}>
            <img
              src={gwsFullLogo}
              alt="Generational Wealth Society"
              className="w-full max-w-4xl h-auto"
            />
          </div>

          {/* scroll hint */}
          <div className="absolute bottom-24 lg:bottom-10 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3">
            <span className="text-[10px] tracking-[0.4em] uppercase" style={{ color: 'rgba(247,245,241,0.35)' }}>
              Scroll
            </span>
            <span className="block w-px h-10 overflow-hidden">
              <span className="gws-scrollline block w-px h-full" style={{ backgroundColor: COPPER }} />
            </span>
          </div>
        </div>
      ),
    },

    /* 02  —  Manifesto */
    {
      bg: PAPER,
      grow: true,
      content: (
        <div className="relative flex min-h-screen w-full items-center px-8 lg:px-28 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_11vw_+_0.5rem)] pb-24 lg:pt-0 lg:pb-0">
          <Ghost n="01" text="WHO WE ARE" />
          <div className="max-w-7xl w-full mx-auto">
            {/* Başlık */}
            <h2
              className="rv gws-display text-4xl md:text-6xl lg:text-[5.2rem] leading-[1.05] mb-5 lg:mb-16"
              style={{ '--d': '0.18s', color: GREEN }}
            >
              Wealth is built for <em style={{ color: COPPER }}>future generations,</em>
              <br className="hidden md:block" />{' '}
              by each generation.
            </h2>

            {/* İki Kolon - Eşit */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Sol Kolon */}
              <div className="space-y-6">
                <p
                  className="rv text-base lg:text-[1.25rem] font-light leading-relaxed"
                  style={{ '--d': '0.35s', color: SLATE }}
                >
                  Generational Wealth Society is a private circle for people who think in
                  decades. We create a safe space where the next generation can discover who they are and
                  what they can bring to the table.
                </p>
                <p
                  className="rv text-base lg:text-[1.25rem] font-light leading-relaxed"
                  style={{ '--d': '0.5s', color: SLATE }}
                >
                  Inheriting is where the story starts for our members. Next is finding purpose, building identity, and carrying the legacy forward.
                </p>
              </div>

              {/* Founders bubble  —  the "Who is in the room?" balloon, on green. The
                  photo sits beside the names, keeping the content box wider than it is
                  tall so the blob reads as the same wide oval as the reference. */}
              <div className="flex justify-center lg:justify-end lg:absolute lg:right-[64px] min-[1250px]:right-[100px] min-[1350px]:right-[160px] lg:bottom-[70px] lg:w-[22rem] min-[1250px]:w-[31rem]">
                <div className="rv relative w-full max-w-[31rem] lg:max-w-none" style={{ '--d': '0.55s' }}>
                  {/* thought-cloud tail  —  drifting dots */}
                  <div
                    aria-hidden
                    className="gws-blob-dot hidden absolute -bottom-[60px] left-16 z-20 h-7 w-7"
                    style={{ background: 'rgba(26, 68, 31, 0.30)', border: '1px solid rgba(228,164,131,0.3)' }}
                  />
                  <div
                    aria-hidden
                    className="gws-blob-dot hidden absolute -bottom-[92px] left-9 z-20 h-4 w-4"
                    style={{ background: 'rgba(26, 68, 31, 0.30)', border: '1px solid rgba(228,164,131,0.3)', animationDelay: '-4s', animationDuration: '8s' }}
                  />
                  <div className="relative z-10 px-8 pt-9 pb-12 min-[1250px]:px-14 min-[1250px]:pt-10 min-[1250px]:pb-16">
                    {/* Balloon shell  —  grown 7.5% past the content box on every side */}
                    <div
                      aria-hidden
                      className="gws-blob absolute inset-[-7.5%] -z-10"
                      style={{
                        background: 'rgba(26, 68, 31, 0.30)',
                        boxShadow: '0 40px 80px -30px rgba(0,0,0,0.5)',
                      }}
                    />
                    {/* Photo centred; hand-drawn arrows run from each person in it down
                        to their name  —  left one to Nafi, right one to Özge */}
                    <div className="flex flex-col items-center">
                      <div className="relative">
                        <img
                          src="/founder-gws-p.png"
                          alt="GWS founders  —  Nafi Emir Sürmeli and Özge Doğan"
                          className="w-36 sm:w-40 min-[1250px]:w-48 rounded-xl"
                        />
                        {/* Callout swoosh  —  tail at the right of Nafi's head, arcing over
                            Özge's head, tipped 15° so it points down-right at the label.
                            The shaft runs into the head so the two shapes overlap  —  no
                            seam between them. */}
                        <svg
                          aria-hidden
                          className="pointer-events-none absolute w-16 min-[1250px]:w-[76px]"
                          style={{ left: '42%', top: '16%', transform: 'rotate(-22deg)' }}
                          viewBox="0 0 100 55"
                          fill={SLATE}
                        >
                          <path d="M 16 40 C 30 24, 52 13, 78 14 L 78 23 C 52 21, 34 28, 16 40 Z" />
                          <path d="M 75 7.5 L 94 18.5 L 75 29.5 C 76.8 22, 76.8 15, 75 7.5 Z" />
                        </svg>
                        {/* the label the swoosh points at */}
                        <span
                          className="absolute whitespace-nowrap uppercase text-base sm:text-xl min-[1250px]:text-[22px]"
                          style={{ left: '80%', top: '10%', color: SLATE, fontWeight: 700, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}
                        >
                          Our Founders
                        </span>
                        {/* Name arrows below 1400px  —  anchored to the photo itself, so at
                            every width they spring exactly from where the photo starts and
                            ends. At 1400px+ the original hand-tuned layout takes over. */}
                        <div
                          aria-hidden
                          className="absolute h-10 w-10 sm:h-12 sm:w-12 min-[1250px]:hidden"
                          style={{
                            left: 0,
                            top: '100%',
                            backgroundColor: SLATE,
                            transform: 'translate(-55%, -22%) scaleX(-1)',
                            WebkitMaskImage: 'url(/arrow-1.png)',
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskSize: 'contain',
                            WebkitMaskPosition: 'center',
                            maskImage: 'url(/arrow-1.png)',
                            maskRepeat: 'no-repeat',
                            maskSize: 'contain',
                            maskPosition: 'center',
                          }}
                        />
                        <div
                          aria-hidden
                          className="absolute h-10 w-10 sm:h-12 sm:w-12 min-[1250px]:hidden"
                          style={{
                            right: 0,
                            top: '100%',
                            backgroundColor: SLATE,
                            transform: 'translate(55%, -22%)',
                            WebkitMaskImage: 'url(/arrow-1.png)',
                            WebkitMaskRepeat: 'no-repeat',
                            WebkitMaskSize: 'contain',
                            WebkitMaskPosition: 'center',
                            maskImage: 'url(/arrow-1.png)',
                            maskRepeat: 'no-repeat',
                            maskSize: 'contain',
                            maskPosition: 'center',
                          }}
                        />
                      </div>
                      <div className="flex min-[1250px]:hidden w-full items-start justify-between pt-8 sm:pt-10">
                        {/* Nafi */}
                        <div className="flex flex-col items-center">
                          <span className="text-base sm:text-lg" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            Nafi Emir Sürmeli
                          </span>
                          <span className="mt-1 text-sm" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            4th Gen
                          </span>
                          <a href="https://www.linkedin.com/in/nafi-emir-s%C3%BCrmeli/" target="_blank" rel="noopener noreferrer" aria-label="Nafi Emir Sürmeli on LinkedIn" className="inline-flex transition-opacity hover:opacity-70 mt-1">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill={SLATE} aria-hidden>
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                              </svg>
                            </a>
                        </div>

                        {/* Özge */}
                        <div className="flex flex-col items-center">
                          <span className="text-base sm:text-lg" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            Özge Doğan
                          </span>
                          <span className="mt-1 text-sm" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            2nd Gen
                          </span>
                          <a href="https://www.linkedin.com/in/%C3%B6zge-do%C4%9Fan-74b87923b/" target="_blank" rel="noopener noreferrer" aria-label="Özge Doğan on LinkedIn" className="inline-flex transition-opacity hover:opacity-70 mt-1">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill={SLATE} aria-hidden>
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                              </svg>
                            </a>
                        </div>
                      </div>

                      {/* 1400px+  —  the original hand-tuned layout, untouched */}
                      <div className="hidden min-[1250px]:flex w-full items-start justify-between">
                        {/* Nafi  —  Özge's mirror image: same arrow flipped, same offsets reversed */}
                        <div className="flex flex-col items-center -mt-7 -translate-x-6">
                          <div
                            aria-hidden
                            className="-mt-3 h-14 w-14"
                            style={{
                              backgroundColor: SLATE,
                              transform: 'translateX(16px) scaleX(-1)',
                              WebkitMaskImage: 'url(/arrow-1.png)',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskSize: 'contain',
                              WebkitMaskPosition: 'center',
                              maskImage: 'url(/arrow-1.png)',
                              maskRepeat: 'no-repeat',
                              maskSize: 'contain',
                              maskPosition: 'center',
                            }}
                          />
                          <span className="text-xl" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            Nafi Emir Sürmeli
                          </span>
                          <span className="mt-1 text-sm" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            4th Gen
                          </span>
                          <a href="https://www.linkedin.com/in/nafi-emir-s%C3%BCrmeli/" target="_blank" rel="noopener noreferrer" aria-label="Nafi Emir Sürmeli on LinkedIn" className="inline-flex transition-opacity hover:opacity-70 mt-1">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill={SLATE} aria-hidden>
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                              </svg>
                            </a>
                        </div>

                        {/* Özge  —  bottom right, tucked in close to the photo */}
                        <div className="flex flex-col items-center -mt-4 -translate-x-2">
                          <div
                            aria-hidden
                            className="-mt-6 h-14 w-14"
                            style={{
                              backgroundColor: SLATE,
                              transform: 'translateX(-16px)',
                              WebkitMaskImage: 'url(/arrow-1.png)',
                              WebkitMaskRepeat: 'no-repeat',
                              WebkitMaskSize: 'contain',
                              WebkitMaskPosition: 'center',
                              maskImage: 'url(/arrow-1.png)',
                              maskRepeat: 'no-repeat',
                              maskSize: 'contain',
                              maskPosition: 'center',
                            }}
                          />
                          <span className="text-xl" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            Özge Doğan
                          </span>
                          <span className="mt-1 text-sm" style={{ color: SLATE, fontFamily: "'DynaPuff', 'Figtree', sans-serif" }}>
                            2nd Gen
                          </span>
                          <a href="https://www.linkedin.com/in/%C3%B6zge-do%C4%9Fan-74b87923b/" target="_blank" rel="noopener noreferrer" aria-label="Özge Doğan on LinkedIn" className="inline-flex transition-opacity hover:opacity-70 mt-1">
                            <svg width="15" height="15" viewBox="0 0 24 24" fill={SLATE} aria-hidden>
                                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                              </svg>
                            </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },

    /* 03  —  Who is in the room */
    {
      bg: `radial-gradient(110% 140% at 10% 100%, ${GREEN} 0%, ${GREEN_DEEP} 60%, #0c2410 100%)`,
      grow: true,
      content: (
        <div className="relative flex lg:min-h-screen w-full items-center px-8 lg:px-28 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_11vw_+_0.5rem)] pb-16 lg:py-16">
          <Ghost n="02" text="THE SOCIETY" light />
          <div className="max-w-7xl w-full mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              <div className="lg:col-span-7">
                <h2
                  className="rv gws-display text-4xl md:text-6xl lg:text-[5.2rem] leading-[1.05]"
                  style={{ '--d': '0.15s', color: COPPER }}
                >
                  {/* the italic's forward lean eats the word space, so buy it back */}
                  <em className="mr-[0.14em]">Good vibes</em> <span style={{ color: '#f7f5f1' }}>only</span>
                </h2>

                <p
                  className="rv mt-4 lg:mt-10 max-w-xl text-base lg:text-[1.25rem] font-light leading-relaxed"
                  style={{ '--d': '0.35s', color: PAPER }}
                >
                  Our members are passionate and genuinely there for each other. We are
                  transparent, interested in creating value and always chasing something
                  we want to learn: a new skill, a new field, a better version of
                  ourselves.
                </p>
                <p
                  className="rv mt-5 max-w-xl text-base lg:text-[1.25rem] font-light leading-relaxed"
                  style={{ '--d': '0.45s', color: PAPER }}
                >
                  In this society, we are curious about the world, and excited for the future. Most of us are very aware of what we were given, and instead
                  of leaning on it we prefer growing it.
                </p>
              </div>

              {/* speech bubble */}
              <div className="lg:col-span-5 flex justify-center lg:justify-end lg:absolute lg:right-[64px] min-[1250px]:right-[100px] min-[1350px]:right-[160px] lg:bottom-[130px] lg:w-[22rem] min-[1250px]:w-[28rem] min-[1350px]:w-[31rem]">
                <div className="rv relative w-full max-w-[31rem] lg:max-w-none" style={{ '--d': '0.55s' }}>
                  {/* thought-cloud tail  —  drifting dots */}
                  <div
                    aria-hidden
                    className="gws-blob-dot hidden absolute -bottom-[60px] left-16 z-20 h-7 w-7"
                    style={{ background: 'rgba(247,245,241,0.7)', border: '1px solid rgba(228,164,131,0.3)' }}
                  />
                  <div
                    aria-hidden
                    className="gws-blob-dot hidden absolute -bottom-[92px] left-9 z-20 h-4 w-4"
                    style={{ background: 'rgba(247,245,241,0.7)', border: '1px solid rgba(228,164,131,0.3)', animationDelay: '-4s', animationDuration: '8s' }}
                  />
                  <div className="relative z-10 px-8 py-10 min-[1250px]:px-14 min-[1250px]:py-16">
                    {/* Balloon shell  —  grown 7.5% past the content box on every side */}
                    <div
                      aria-hidden
                      className="gws-blob absolute inset-[-7.5%] -z-10"
                      style={{
                        background: 'rgba(247,245,241,0.7)',
                        boxShadow: '0 40px 80px -30px rgba(0,0,0,0.5)',
                      }}
                    />
                    <h3
                      className="gws-display text-[1.2rem] min-[1250px]:text-2xl mb-6 uppercase"
                      style={{ color: GREEN, fontVariationSettings: "'SOFT' 100, 'WONK' 0, 'opsz' 144, 'wght' 700" }}
                    >
                      Who is in the room?
                    </h3>
                    <p
                      className="text-[0.945rem] min-[1250px]:text-[16.2px] font-light leading-relaxed"
                      style={{ color: GREEN }}
                    >
                      There are {memberCount} of us right now, mostly between 21 and 40. Second,
                      third, or later generations born into wealth from Türkiye, Europe, Middle East and US.
                    </p>
                    <p
                      className="mt-5 text-[0.945rem] min-[1250px]:text-[16.2px] font-light leading-relaxed"
                      style={{ color: GREEN }}
                    >
                      Some of us already sit at the table in the family business. Some are
                      getting ready to. Some are building something entirely their own.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },

    /* 04  —  What we do */
    {
      bg: PAPER,
      grow: true,
      content: (
        <div className="relative flex w-full items-center px-8 lg:px-20 pt-[calc(clamp(3rem,3rem_+_(1400px_-_100vw)_*_0.04,6rem)_+_11vw_+_0.5rem)] pb-16 lg:pb-12">
          <Ghost n="03" text="WHAT WE DO" />
          <div className="max-w-7xl w-full mx-auto">
            <h2
              className="rv gws-display mb-4 lg:mb-10 text-4xl md:text-6xl lg:text-[5.2rem] leading-tight"
              style={{ "--d": "0.15s", color: GREEN }}
            >
              Closing the gap between
              <br className="hidden md:block" /> <em style={{ color: COPPER }}>knowing and being.</em>
            </h2>

            <div className="space-y-6 lg:space-y-8">
              {[
                {
                  title: 'Travel',
                  desc: 'Exploring the world, together — new geographies, cultural trips, and sometimes just for leisure. We widen our view of the world.',
                  flip: false,
                  events: [
                    {
                      title: 'From Zeugma to the Table: A Gaziantep Journey',
                      excerpt: 'Hosted by Zeynep Topçuoğlu in collaboration with RENA Travel, this three-day journey invites GWS members to experience Gaziantep through its stories, flavors, and living heritage.',
                      host: 'Zeynep Topçuoğlu',
                      date: '3 Apr 2026',
                      location: 'Divan Hotel',
                      img: 'https://archaeology.org/wp-content/uploads/2012/10/roman-mosaic.jpg',
                    },
                    {
                      title: "A Collector's Dream: Audemars Piguet & Patek Philippe",
                      excerpt: 'Switzerland is the home of fine watchmaking. This June, we are discovering Geneva. We will step inside two of the most legendary manufactures in the world, Audemars Piguet and Patek Philippe, for a rare behind-the-scenes experience.',
                      host: 'Xavier Bonna',
                      date: '4 Jun 2026',
                      location: 'The Ritz-Carlton Hotel de la Paix, Geneva',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2026/03/atelier.jpg',
                    },
                    {
                      title: 'Recharge: Decoding Brain & Body',
                      excerpt: 'We’ve planned a two-day retreat to slow down and dive deeper into wellbeing through shared learning and therapy.',
                      host: 'Generational Wealth Society',
                      date: '19 Sep 2026',
                      location: 'NG Enjoy Sapanca',
                      img: 'https://cdn.trav3l.net/sapancakonaklamarehberi.com/files/hotels/459/ng-sapanca-drone-20-f.jpg',
                    },
                    {
                      title: 'GWS Takes Over Mykonos',
                      excerpt: 'For our hardworking NextGens, we planned the perfect island getaway. Good food, dancing to the best sounds at Alemagou and Scorpios, and soaking up the sun on Greek shores… We are stopping time for a weekend!',
                      host: 'Generational Wealth Society',
                      date: '7 Aug 2026',
                      location: 'Mykonos, Greece',
                      img: 'https://yzzynxgixflgjzvotiae.supabase.co/storage/v1/object/public/event-images/event-1785227737732.jpg',
                    },
                  ],
                },
                {
                  title: 'Intergenerational Talks',
                  desc: "Under Chatham House Rule, family members across generations talk honestly about what really happened — the transitions, the setbacks, the mistakes. We learn from each other's experiences.",
                  flip: true,
                  events: [
                    {
                      title: 'Intergenerational Talks: Alaton Family',
                      excerpt: 'An intimate conversation on succession, responsibility, and the realities of generational transition with Eros, Atlas and Leyla.',
                      host: 'Eros Alaton & Atlas Alaton',
                      date: '11 Feb 2026',
                      location: 'Alarko Holding',
                      img: 'https://www.fortuneturkey.com/wp-content/uploads/2024/12/AlarkoHoldingBina-2048x1341.jpg',
                    },
                    {
                      title: 'Intergenerational Talks: Saltık Family',
                      excerpt: 'We are welcomed into the home of the Saltık family. Kaan, Burak, and Beliz, spanning two generations of Koluman Holding, will share what it really looks like to lead and grow together as a family.',
                      host: 'Beliz Saltık & Burak Saltık',
                      date: '28 Jun 2026',
                      location: 'Günyüzü Konakları, Beykoz',
                      img: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcR2YJPUUGpPn6oMKUkCRjXmBM4BrRvqj7esiw&s',
                    },
                  ],
                },
                {
                  title: 'Business Table',
                  desc: 'Closed-door sessions with industry experts, academics and professionals from different sectors and background, so we can be one step ahead in our businesses.',
                  flip: false,
                  events: [
                    {
                      title: "Business Table: What's Happening in the M&A Market?",
                      excerpt: "Global uncertainty is reshaping how family businesses grow, exit, and position themselves. In this session, Levent Bosut of inHERA Capital shares what's actually moving in the M&A market and what it means for family members.",
                      host: 'inHERA Capital',
                      date: '9 Apr 2026',
                      location: 'inHERA Office, Levent',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2026/03/event-loc.jpeg',
                    },
                  ],
                },
                {
                  title: 'Investor Mindset',
                  desc: "Wealth management and investing, demystified. Understanding markets, sharpening our judgment, learning to actually manage what we'll one day be responsible for.",
                  flip: true,
                  events: [
                    {
                      title: 'Understanding Private Markets',
                      excerpt: 'Leading voices from Hamilton Lane and family members will explore emerging trends secondary market dynamics and the evolving role of private capital of long term portfolios.',
                      host: 'Karman Beyond Family Office',
                      date: '23 Oct 2025',
                      location: 'Mandarin Oriental Bosphorus',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2025/10/mandarin-ist.jpeg',
                    },
                    {
                      title: 'Structuring in Wealth Management',
                      excerpt: 'A closed session exploring global structuring practices with senior leaders from CITCO. Designed for families navigating cross-border wealth, governance, and long-term planning.',
                      host: 'Karman Beyond Family Office',
                      date: '8 Dec 2025',
                      location: 'Raffles Hotel, Zorlu Center',
                      img: 'https://www.lightfoottravel.com/cdn-cgi/image/width=1800,height=,quality=80,format=webp/https://lft-dev-images.s3.eu-west-1.amazonaws.com/public/property/raffles-istanbul.jpg',
                    },
                    {
                      title: 'Family Capital: Venture Investing',
                      excerpt: "How do you convince a legacy family to bet on the future? Defne Kocabıyık Narter invites us to Borusan Holding to share how she built Borusan's venture arm from the inside.",
                      host: 'Defne Kocabıyık Narter',
                      date: '27 Mar 2026',
                      location: 'Borusan Holding',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2026/03/gws-event.jpeg',
                    },
                    {
                      title: 'WeShape NextGen Ecosystem Connect',
                      excerpt: 'WeShape Entrepreneurship Program, brought to life by CCN Holding as a social impact initiative for university students, has completed its second cycle and we are invited to meet with the finalists.',
                      host: 'Berfin Çeçen Şenol & İbrahim Çeçen Vakfı',
                      date: '14 May 2026',
                      location: 'The Cube, Altunizade',
                      img: 'https://yzzynxgixflgjzvotiae.supabase.co/storage/v1/object/public/event-images/event-1778652820256.jpg',
                    },
                    {
                      title: 'Alternative Investments: Private Markets Update',
                      excerpt: 'The headlines are loud but what is actually happening in private markets? We are hosting Daniel from Hamilton Lane for an unfiltered conversation about the Iran war and its impact on 2026 projections, the real story behind private debt and what to watch out for private market portfolios.',
                      host: 'Karman Beyond Family Office',
                      date: '8 May 2026',
                      location: 'Zorlu Levent 199',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2026/04/event-gws-2.jpeg',
                    },
                  ],
                },
                {
                  title: 'Social Gatherings',
                  desc: 'Time together, new places, good food, and better company… The kind of sharing that turn people from acquaintances into friends.',
                  flip: false,
                  events: [
                    {
                      title: 'A Celebration for Generational Wealth Society',
                      excerpt: 'This private evening brings together members to celebrate the beginning of the Generational Wealth Society.',
                      host: 'Nafi Emir Sürmeli - Özge Doğan',
                      date: '24 Oct 2025',
                      location: 'Kuzguncuk',
                      img: 'https://media-cdn.tripadvisor.com/media/photo-s/0c/25/c1/c7/bogazdan-manzaralar.jpg',
                    },
                    {
                      title: 'From Screen to Dance Floor',
                      excerpt: "You are invited to Salon İKSV for a special evening of iconic film music performed by Seran Bilgi. From James Bond to Mamma Mia, The Wizard of Oz and A Star Is Born, cinema's greatest melodies will make you dance.",
                      host: 'Generational Wealth Society & İKSV',
                      date: '14 Apr 2026',
                      location: 'Salon İKSV, Beyoğlu',
                      img: 'https://lifeartsanat.com/wp-content/uploads/2022/09/img_1562.jpg',
                    },
                    {
                      title: 'Dinner Table: Murmures',
                      excerpt: 'For three nights only, the historic Passage Petit Le Champs in Beyoğlu transforms into Murmures, a fantasy bistrot imagined by Tanrı Misafiri, Geyik, and ZIVO. A curated tasting menu, signature cocktails, and many strangers coming together at a dinner table. We reserved your seats for the opening night.',
                      host: 'Generational Wealth Society & Tanrı Misafiri',
                      date: '16 Apr 2026',
                      location: 'Petit Champs Pasajı, Beyoğlu',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2026/04/event-gws.jpeg',
                    },
                    {
                      title: 'The Hammam Ritual',
                      excerpt: "A 500-year-old Ottoman bathhouse, a guided museum tour followed by a scrub and foam massage. We are spending the afternoon at Zeyrek Çinili Hamam, recently named one of TIME's 100 greatest places in the world.",
                      host: 'Generational Wealth Society',
                      date: '17 May 2026',
                      location: 'Zeyrek Çinili Hamam, Fatih',
                      img: 'https://dacistanbul.com/wp-content/uploads/2023/09/zeyrek-cinili-hamam_POST-1-_hadiye-cangokce-1600x1102.jpg',
                    },
                    {
                      title: 'Housewarming Party: Writers Bar, Raffles Istanbul',
                      excerpt: 'We are coming together with our members and friends to celebrate our first Home. We will dance to the music and raise our glasses to genuine connections. Curious about the dress code? Register and spin the wheel to find out!',
                      host: 'Generational Wealth Society',
                      date: '11 Jun 2026',
                      location: 'Writers Bar, Raffles Hotel',
                      img: 'https://m.ahstatic.com/is/image/accorhotels/aja_p_7229-17?wid=1920',
                    },
                  ],
                },
                {
                  title: 'Culture Building',
                  desc: 'Sports, art, and culture, not just as hobbies but as how we sharpen our thinking and deepen how we see the world.',
                  flip: true,
                  events: [
                    {
                      title: 'Wealth and Wisdom Trivia Night',
                      excerpt: 'We are inviting you to a fun evening where your inner child and competitive spirit come out to play, while your adult self enjoys delicious food and cocktails.',
                      host: 'Generational Wealth Society',
                      date: '20 Nov 2025',
                      location: 'Raffles Hotel, Writers Bar',
                      img: 'https://m.ahstatic.com/is/image/accorhotels/aja_p_7090-33?qlt=82&wid=575&ts=1709798980680&dpr=on,2.625',
                    },
                    {
                      title: 'Inside The Cube: Art & Collecting',
                      excerpt: 'Join us for an inspiring Friday evening exploring digital art, collecting, and the ideas shaping family legacy followed by wine, music, and warm conversations.',
                      host: 'Berfin Çeçen Şenol',
                      date: '12 Dec 2025',
                      location: 'FAMI - Altunizade',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2025/12/venue.jpeg',
                    },
                    {
                      title: 'Wine and Dine: Suvla',
                      excerpt: 'Together with Selim Ellialtı, we gather around the table to enjoy wine and pizza, while exploring Suvla’s story and the wine culture in Türkiye.',
                      host: 'Generational Wealth Society',
                      date: '24 Jan 2026',
                      location: 'Kanyon AVM, Suvla',
                      img: 'https://lh6.googleusercontent.com/-0IDyUzEePsI/Vzx1Mq-jwkI/AAAAAAAAMwA/89xcLp0AHAYfCbNyHGy6Ew29dT5KuwPdwCJkC/s1600-w640/',
                    },
                    {
                      title: 'GWS Padel Cup',
                      excerpt: 'Join us for an energetic Sunday at Opadel as we come together for the first GWS Cup. Whether you’re a seasoned player or picking up a racket for the first time, there’s a spot for you on the court!',
                      host: 'Generational Wealth Society',
                      date: '15 Mar 2026',
                      location: 'Opadel, Maslak Hom Center',
                      img: 'https://opadel.co/public/template/images/gallery/gallery-2.webp',
                    },
                    {
                      title: 'Sunday-Funday: Padel',
                      excerpt: 'Join us for a Sunday Padel session! From seasoned pros to absolute beginners everyone is welcome on the court.',
                      host: 'Generational Wealth Society',
                      date: '19 Apr 2026',
                      location: 'O’Padel, Maslak Hom Center',
                      img: 'https://opadel.co/public/template/images/gallery/gallery-3.webp',
                    },
                    {
                      title: 'The Art of the Cigar',
                      excerpt: 'Together with Ömer Doğancı a.k.a Dr. PURO, we will explore the culture behind the cigar while sipping selected range of fine whiskies.',
                      host: 'Yalın Kılıç & Nafi Emir Sürmeli',
                      date: '7 May 2026',
                      location: 'Kuzguncuk, Üsküdar',
                      img: 'https://cdnuploads.aa.com.tr/uploads/Contents/2015/10/09/thumbs_b_c_3525641b3ca8955c3fe96f8fe8ff73f8.jpg',
                    },
                    {
                      title: 'Sculpture Journey',
                      excerpt: 'We’re invited to Bodrum Golf Club for an evening among sculpture, curated by Aslı Özok. Ayla Turan and Kemal Tufan’s works turn the course into a living open-air exhibition one to walk through, not just admire.',
                      host: 'Generational Wealth Society',
                      date: '13 Jul 2026',
                      location: 'Bodrum Golf and Tennis Club, Ortakent',
                      img: 'https://yzzynxgixflgjzvotiae.supabase.co/storage/v1/object/public/event-images/event-1783776348797.jpg',
                    },
                    {
                      title: 'Sailing 101',
                      excerpt: "We are welcomed aboard by Levent Peynirci, the most decorated skipper in Turkish sailing, for an evening on open water. We'll sail toward Burgazada for dinner at Kalpazankaya, watching the sunset as we dine.",
                      host: 'Generational Wealth Society',
                      date: '28 Jul 2026',
                      location: 'Kalamış Marina, Kadıköy',
                      img: 'https://karmanbeyond.com/wp-content/uploads/2026/07/sail.jpg',
                    },
                  ],
                },
              ]
                .map((group) => ({
                  ...group,
                  events: deckEvents ? deckEvents.filter((ev) => ev.category === group.title) : group.events,
                }))
                .map(({ title, desc, flip, events }, i) => (
                <div
                  key={title}
                  /* 5/16 to the copy, 11/16 to the cards. The tracks are sized rather
                     than spanned so the pair stays exact through one gap  —  and the
                     ratio is mirrored on flipped rows, since `order` moves the items
                     between tracks but never resizes the tracks themselves. */
                  className={`rv grid grid-cols-1 gap-6 lg:gap-10 items-center ${
                    flip ? 'lg:grid-cols-[11fr_5fr]' : 'lg:grid-cols-[5fr_11fr]'
                  } ${
                    /* the gap is split evenly either side of the rule: the container's
                       space-y supplies the half above, this padding the half below */
                    i > 0 ? 'pt-6 lg:pt-8' : ''
                  }`}
                  style={{
                    '--d': `${0.3 + i * 0.1}s`,
                    ...(i > 0
                      ? {
                          /* painted rather than bordered: border-dashed's mark and gap
                             lengths are the UA's to pick and take no CSS input, so the
                             rule is a repeating gradient  —  2px mark, 10px gap, 1px
                             tall  —  laid along the top edge of the padding box */
                          backgroundImage: `repeating-linear-gradient(to right, ${COPPER} 0 2px, transparent 2px 12px)`,
                          backgroundSize: '100% 1px',
                          backgroundRepeat: 'no-repeat',
                          backgroundPosition: 'left top',
                        }
                      : {}),
                  }}
                >
                  {/* Text Content. min-w-0 on both tracks: a grid item's automatic
                      minimum is its content's min-content width, and the band's card
                      row is far wider than its column  —  without this it forces the
                      column open and the 5/11 split collapses. */}
                  <div className={`min-w-0 ${flip ? 'lg:order-2' : ''}`}>
                    <h3 className="gws-display text-xl lg:text-2xl leading-tight mb-2" style={{ color: GREEN }}>
                      {title}
                    </h3>
                    <p className="text-base lg:text-[1.25rem] font-light leading-relaxed" style={{ color: SLATE }}>
                      {desc}
                    </p>
                  </div>

                  {/* Two ways to show the same cards, chosen by count and width.
                      Below lg only one card fits across, so anything with more than a
                      single event runs as a band rather than a tall stack. From lg a
                      track of three or fewer goes back to a static row  —  two columns
                      when there are exactly two, so the pair widens instead of leaving
                      a slot empty  —  and only the longer ones keep running. */}
                  <div className={`min-w-0 ${flip ? 'lg:order-1' : ''}`}>
                    {events.length >= 2 && (
                      <DraggableMarquee events={events}>
                        {events.map((ev, ei) => (
                          <div
                            key={ei}
                            className="w-full flex-shrink-0 lg:w-[calc((100%-2rem)/3)]"
                          >
                            <EventCard ev={ev} />
                          </div>
                        ))}
                      </DraggableMarquee>
                    )}
                    {events.length <= 2 && (
                      <div
                        className={`grid-cols-1 gap-3 lg:gap-4 ${
                          events.length >= 2 ? 'hidden lg:grid' : 'grid'
                        } lg:grid-cols-2`}
                      >
                        {/* keyed by position, not title  —  placeholder cards read "TBA" */}
                        {events.map((ev, ei) => (
                          <EventCard key={ei} ev={ev} />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },

    /* 05  —  How we stay together (split) */
    {
      bg: PAPER,
      grow: true,
      content: (
        <div className="relative grid min-h-screen w-full grid-cols-1 lg:grid-cols-2">
          {/* Digital */}
          {/* the copy stops well short of the fold below lg  —  stacked, the seam reads
              as this half's floor, and it needs room to be seen as one */}
          <div className="relative flex items-center justify-center lg:items-start lg:pt-[34vh] px-8 lg:px-16 xl:px-20 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_11vw_+_0.5rem)] pb-24 lg:pb-12" style={{ backgroundColor: PAPER }}>
            <Ghost n="04a" text="DIGITAL HOME" small centered />
            <div className="max-w-md lg:max-w-sm xl:max-w-md text-center">
              <h3
                className="rv gws-display text-4xl lg:text-5xl xl:text-6xl leading-tight"
                style={{ '--d': '0.22s', color: GREEN }}
              >
                The GWS App
              </h3>
              <p
                className="rv mt-4 lg:mt-6 text-base lg:text-[1.25rem] font-light leading-relaxed"
                style={{ '--d': '0.3s', color: GREEN }}
              >
                Everything runs through here  —  event announcements, RSVPs, and a News
                Agent that developed by using AI keeps us on top of what's happening
                around the World. It's how our society works day to day.
              </p>

              {/* Store badges */}
              <div className="rv mt-7 flex items-center justify-center gap-3" style={{ '--d': '0.38s' }}>
                <a
                  href="https://apps.apple.com/app/generational-wealth-society/id6753958038"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Download on the App Store"
                  className="inline-flex items-center gap-2.5 rounded-lg px-4 py-2 transition-opacity hover:opacity-80"
                  style={{ backgroundColor: GREEN }}
                >
                  <svg width="22" height="26" viewBox="0 0 384 512" aria-hidden>
                    <path fill="#fff" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.7-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
                  </svg>
                  <span className="flex flex-col items-start leading-none">
                    <span className="text-[9px] font-medium text-white/80">Available on the</span>
                    <span className="mt-0.5 text-[15px] font-semibold text-white">App Store</span>
                  </span>
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.karmanbeyond.gws"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Get it on Google Play"
                  className="inline-flex items-center gap-2.5 rounded-lg px-4 py-2 transition-opacity hover:opacity-80"
                  style={{ backgroundColor: GREEN }}
                >
                  <svg width="22" height="24" viewBox="0 0 512 512" aria-hidden>
                    <path fill="#fff" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5 18-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
                  </svg>
                  <span className="flex flex-col items-start leading-none">
                    <span className="text-[9px] font-medium text-white/80">Get it on</span>
                    <span className="mt-0.5 text-[15px] font-semibold text-white">Google Play</span>
                  </span>
                </a>
              </div>

              {/* Watch the video button - hidden until video is ready
              <button
                type="button"
                className="rv group mt-8 inline-flex items-center gap-4"
                style={{ '--d': '0.54s' }}
              >
                <span
                  className="relative flex h-12 w-12 items-center justify-center rounded-full transition-transform duration-500 group-hover:scale-110"
                  style={{ backgroundColor: GREEN }}
                >
                  <span
                    aria-hidden
                    className="gws-ping absolute inset-0 rounded-full"
                    style={{ border: `1px solid ${GREEN}` }}
                  />
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" className="ml-0.5">
                    <path d="M0 0.8v12.4a.8.8 0 0 0 1.22.68l10-6.2a.8.8 0 0 0 0-1.36l-10-6.2A.8.8 0 0 0 0 .8Z" fill={COPPER} />
                  </svg>
                </span>
                <span
                  className="text-xs tracking-[0.25em] uppercase font-medium transition-opacity duration-300 group-hover:opacity-60"
                  style={{ color: GREEN }}
                >
                  Watch the video
                </span>
              </button>
              */}
            </div>
            <SeamHorizontal />
          </div>

          {/* Physical */}
          <div
            /* the extra 2.5rem in the top padding is the ghost's mt-10 below  —  the
               padding has to clear the ghost's new resting place, not its old one */
            className="relative flex items-center justify-center lg:items-start lg:pt-[34vh] overflow-hidden px-8 lg:px-16 xl:px-20 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_11vw_+_3rem)] pb-12 lg:pb-12"
            style={{ background: `radial-gradient(120% 120% at 100% 0%, ${GREEN} 0%, ${GREEN_DEEP} 70%)` }}
          >
            {/* stacked below lg this ghost sits right under the seam, so it drops clear
                of it; from lg the halves are side by side and it lines up again */}
            <Ghost n="04b" text="PHYSICAL HOME" light small centered className="mt-10 lg:mt-0" />
            <div className="relative max-w-md lg:max-w-sm xl:max-w-md text-center">
              <h3
                className="rv gws-display text-4xl lg:text-5xl xl:text-6xl leading-tight text-[#f7f5f1]"
                style={{ '--d': '0.32s' }}
              >
                Writer's Bar
              </h3>
              <p
                className="rv mt-4 lg:mt-6 text-base lg:text-[1.25rem] font-light leading-relaxed"
                style={{ '--d': '0.46s', color: PAPER }}
              >
                Our own corner of the city  —  a bar, a piano, a fireplace, cigars if that's
                your thing. You can book your visit through the app, bring up to three
                friends, and settle in. This is where our talks, parties, and game nights
                also happen, with our own menu and 10% discount.
              </p>
              <div
                className="rv mt-5 flex items-center justify-center"
                style={{ '--d': '0.54s' }}
              >
                <img
                  src="/raffles.png"
                  alt="Raffles Istanbul"
                  className="w-40 lg:w-48"
                  style={{
                    filter: 'brightness(0) invert(1)',
                    opacity: 0.9
                  }}
                />
              </div>
            </div>
          </div>

          <Seam />
        </div>
      ),
    },

    /* 06  —  Values */
    {
      bg: PAPER,
      grow: true,
      content: (
        <div
          /* this ghost is set at 8.8vw, not the usual 11vw  —  the top padding has to
             reserve that smaller height or it opens a gap under the word */
          className="relative flex lg:min-h-screen w-full items-center px-8 lg:px-28 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_8.8vw_+_0.5rem)] pb-12 lg:py-14"
        >
          <Ghost n="05" text="WHAT WE STAND FOR" size="8.8vw" />
          <div className="max-w-6xl w-full mx-auto">
            <h2
              className="rv gws-display mt-0 lg:mt-6 mb-5 lg:mb-16 text-4xl md:text-6xl lg:text-[5.2rem]"
              style={{ '--d': '0.15s', color: GREEN }}
            >
              Society values
            </h2>

            <div className="grid grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-5 lg:gap-6">
              {/* Each card carries one colour per syllable  —  null falls back to the
                  heading green */}
              {[
                ["Discretion", ["Dis", "Cre", "Tion"], [COPPER, COPPER, COPPER], "We appreciate discretion.", "What's shared here stays here including the stories, and the people who tell them. This is a safe space."],
                ["Respect", ["Res", "Pect"], [COPPER, COPPER], "We believe in respect.", "We value each other's boundaries. No judgment, no pressure, no unsolicited advice."],
                ["Commitment", ["Com", "Mit", "Ment"], [COPPER, COPPER, COPPER], "We expect commitment.", "Being here takes time, and effort. We're defined by what we do, so everyone contributes in whatever way they can. We show up for each other, and we celebrate each other."],
                ["Humility", ["Hu", "Mil", "Ity"], [COPPER, COPPER, COPPER], "We encourage humility.", "No matter where you come from or what you do, we are all equal here. Who you are is defined by your character."],
              ].map(([title, syllables, sylColors, creed, desc], i) => (
                <div
                  key={title}
                  className="rv gws-flip h-[260px] sm:h-[320px] lg:h-[380px]"
                  style={{ '--d': `${0.28 + i * 0.09}s` }}
                  tabIndex={0}
                >
                  <div className="gws-flip-inner">
                    {/* Front */}
                    <div
                      className="gws-face items-center justify-center p-5 sm:p-7 lg:p-8"
                      style={{ background: 'rgba(26, 68, 31, 0.10)', border: `3px dashed ${GREEN}` }}
                    >
                      {/* Long arrow, top right  —  the flip hint */}
                      <span
                        className="gws-flip-hint absolute bottom-5 right-5 sm:bottom-7 sm:right-7 lg:bottom-8 lg:right-8"
                        aria-hidden
                      >
                        <svg width="44" height="12" viewBox="0 0 44 12" fill="none">
                          <path
                            className="gws-arrow"
                            d="M0 6 H 41 M 36 1 L 42 6 L 36 11"
                            stroke={GREEN}
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>

                      {/* Letters alternate between the display serif and the sans  —  the
                          mixed-type look of the Communité bag. The parity flips with each
                          line too, so stacked letters never repeat the same face. */}
                      <h3
                        className="uppercase text-center text-4xl sm:text-6xl lg:text-[4.2rem] leading-[1.08]"
                        style={{ color: GREEN }}
                      >
                        {syllables.map((s, si) => (
                          <span
                            key={s}
                            className="block"
                            style={sylColors[si] ? { color: sylColors[si] } : undefined}
                          >
                            {[...s].map((ch, ci) => (
                              <span
                                key={ci}
                                className={(si + ci) % 2 === 0 ? 'gws-display' : 'font-light'}
                              >
                                {ch}
                              </span>
                            ))}
                          </span>
                        ))}
                      </h3>
                    </div>

                    {/* Back  —  one paragraph, centred; the creed opens the sentence */}
                    <div
                      className="gws-face gws-face-back items-center justify-center p-5 sm:p-7 lg:p-8 px-3 sm:px-4 lg:px-5"
                      style={{
                        background: `radial-gradient(150% 150% at 100% 0%, ${GREEN} 0%, ${GREEN_DEEP} 100%)`,
                        border: `3px dashed ${GREEN}`,
                      }}
                    >
                      <p
                        className="text-center text-[13px] sm:text-[15px] lg:text-[17px] font-light leading-relaxed"
                        style={{ color: '#ffffff' }}
                      >
                        {/* fontVariationSettings has to be released here: body pins the
                            wght axis at 500 and an explicit axis value outranks
                            font-weight, so font-bold alone renders at 500 */}
                        <em
                          className="block font-extrabold"
                          style={{ color: COPPER, fontVariationSettings: 'normal' }}
                        >
                          {creed}
                        </em>
                        {desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ),
    },

    /* 06  —  Membership. Original layout, kept for reference  —  see LEGACY_MEMBERSHIP. */
    ...(LEGACY_MEMBERSHIP ? [{
      bg: `radial-gradient(120% 130% at 90% 100%, ${GREEN} 0%, ${GREEN_DEEP} 60%, #0c2410 100%)`,
      content: (
        <div className="relative flex h-full w-full items-center px-8 lg:px-28 py-14">
          <Ghost n="06" text="MEMBERSHIP" light />
          <div className="max-w-5xl w-full mx-auto">
            <h2
              className="rv gws-display mt-0 lg:mt-6 mb-5 lg:mb-14 text-4xl md:text-6xl lg:text-[5.2rem] text-[#f7f5f1]"
              style={{ '--d': '0.15s' }}
            >
              It has to feel right  — <br />
              <span style={{ color: COPPER }}>on both sides.</span>
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-5 gap-y-8 md:gap-x-6">
              {[
                ['Apply', 'Fill out the form.'],
                ['Meet', 'A conversation with our founders'],
                ['Experience', 'Join a GWS event before deciding'],
                ['Join', 'Become a member'],
                ['Set up', 'Your profile goes live on the app'],
              ].map(([title, desc], i) => (
                <div key={title} className="rv group cursor-default md:pt-6 relative" style={{ '--d': `${0.3 + i * 0.1}s` }}>
                  {/* top rule for desktop */}
                  <div className="hidden md:block absolute top-0 left-0 right-0 h-px" style={{ background: 'rgba(247,245,241,0.12)' }}>
                    <div
                      className="h-px w-0 group-hover:w-full transition-all duration-700"
                      style={{ background: COPPER }}
                    />
                  </div>
                  <span className="gws-display text-lg" style={{ color: COPPER }}>{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="mt-2 text-lg lg:text-xl font-normal text-[#f7f5f1]">{title}</h3>
                  <p className="mt-2 text-xs lg:text-sm font-light leading-relaxed" style={{ color: 'rgba(247,245,241,0.5)' }}>
                    {desc}
                  </p>
                </div>
              ))}
            </div>

            <p
              className="rv mt-12 text-xs tracking-[0.25em] uppercase"
              style={{ '--d': '0.85s', color: 'rgba(228,164,131,0.55)' }}
            >
              Founders  —  Nafi Emir Sürmeli · Özge Doğan
            </p>
          </div>
        </div>
      ),
    }] : [{
      /* 06  —  Membership. The winding path, currently shipping. */
      bg: `radial-gradient(120% 130% at 10% 0%, ${GREEN} 0%, ${GREEN_DEEP} 60%, #0c2410 100%)`,
      grow: true,
      content: (
        <div className="relative flex lg:min-h-screen w-full items-center px-8 lg:px-20 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_11vw_+_0.5rem)] pb-16 lg:py-14">
          <Ghost n="06" text="MEMBERSHIP" light />
          <div className="max-w-7xl w-full mx-auto">
            <h2
              className="rv gws-display mt-0 lg:mt-48 text-4xl md:text-6xl lg:text-[5.2rem] text-[#f7f5f1]"
              style={{ '--d': '0.15s' }}
            >
              {/* the italic's forward lean eats the word space, so buy it back */}
              It has to <em className="mr-[0.14em]" style={{ color: COPPER }}>feel right</em> on both sides.
            </h2>

            {/* Desktop  —  winding path. The first node lines up with the heading's left
                edge and the last with the right edge (3/34.3/65.7/97%); each dashed
                segment stops short of the circles. */}
            <div className="relative mt-24 mb-40 hidden lg:block h-[380px]">
              <svg
                className="absolute inset-0 h-full w-full"
                viewBox="0 0 1000 220"
                preserveAspectRatio="none"
                fill="none"
                aria-hidden
              >
                <defs>
                  <clipPath id="gwsPathReveal">
                    <rect className="gws-reveal" x="0" y="0" width="1000" height="220" />
                  </clipPath>
                </defs>
                <g clipPath="url(#gwsPathReveal)">
                  {[0, 1, 2].map((i) => {
                    const x1 = 30 + i * 313.33;
                    const x2 = x1 + 313.33;
                    const y1 = i % 2 === 0 ? 77 : 143;
                    const y2 = i % 2 === 0 ? 143 : 77;
                    const trim = 42;
                    return (
                      <path
                        key={i}
                        d={`M ${x1 + trim},${y1} C ${x1 + 130},${y1} ${x2 - 130},${y2} ${x2 - trim},${y2}`}
                        stroke="rgba(228,164,131,0.5)"
                        /* the run thickens step by step, the way the nodes fill up */
                        strokeWidth={[2, 3, 4][i]}
                        strokeLinecap="round"
                        strokeDasharray="2 9"
                        vectorEffect="non-scaling-stroke"
                      />
                    );
                  })}
                </g>
              </svg>

              <div className="absolute inset-0">
                {[
                  ['Apply', 'Fill out the form.'],
                  ['Meet', 'An introductory conversation with our founders.'],
                  ['Experience', "You're invited to a GWS event, to see what it's actually like before deciding anything."],
                  ['Join', "If it feels right on both sides, you become a member with the membership fee. Your profile goes live on the GWS app, and you're in."],
                ].map(([title, desc], i) => {
                  const isTop = i % 2 === 0;
                  const cx = [3, 34.33, 65.67, 97][i]; /* node centre, mirrors the SVG x  */
                  const isFirst = i === 0;
                  const isLast = i === 3;
                  return (
                    /* Full-size overlay per step: the rv entrance animates transform, which
                       makes this div the containing block for the absolute children  —  so it
                       must span the whole path area, not collapse to zero. pointer-events
                       off so stacked overlays don't rob hover from earlier nodes. */
                    <div
                      key={title}
                      className="rv absolute inset-0 pointer-events-none"
                      style={{ '--d': `${0.45 + i * 0.16}s` }}
                    >
                      <div
                        className="group pointer-events-auto absolute -translate-x-1/2 -translate-y-1/2"
                        style={{ left: `${cx}%`, top: isTop ? '35%' : '65%' }}
                      >
                        <div
                          className="gws-node"
                          style={{ '--nd': `${i * 0.4}s`, '--hd': `${1 + i * 0.9}s` }}
                        >
                          <div
                            className="gws-node-circle relative flex h-16 w-16 items-center justify-center rounded-full"
                            style={{
                              /* the same graduated fill, now in copper  —  the step reads as filling up */
                              background: `rgba(228, 164, 131, ${[0.3, 0.55, 0.8, 1][i]})`,
                              border: `1px solid ${COPPER}`,
                            }}
                          >
                            <span
                              aria-hidden
                              className="gws-halo absolute inset-0 rounded-full"
                              style={{ border: '1px solid #ffffff' }}
                            />
                            <span
                              className="gws-display text-xl"
                              style={{
                                color: GREEN,
                                fontVariationSettings: "'SOFT' 100, 'WONK' 0, 'opsz' 144, 'wght' 700",
                              }}
                            >
                              {i + 1}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* The edge labels hug the container edges  —  Apply shares the
                          heading's left line  —  the middle ones centre on their node */}
                      <div
                        className={`absolute w-72 xl:w-80 ${isFirst ? 'text-left' : isLast ? 'text-right' : 'text-center'}`}
                        style={{
                          ...(isTop ? { bottom: 'calc(65% + 46px)' } : { top: 'calc(65% + 46px)' }),
                          ...(isFirst
                            ? { left: 0 }
                            : isLast
                              ? { right: 0 }
                              : { left: `${cx}%`, transform: 'translateX(-50%)' }),
                        }}
                      >
                        <h3 className="text-xl xl:text-2xl font-normal text-[#f7f5f1]">{title}</h3>
                        <p
                          className="mt-2 text-base lg:text-[1.25rem] font-light leading-relaxed"
                          style={{ color: '#ffffff' }}
                        >
                          {desc}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Mobile  —  the winding path turned vertical: same graduated circles,
                same dotted curves snaking between them, nothing after the last node */}
            <div className="relative mt-6 lg:hidden">
              {[
                ['Apply', 'Fill out the form.'],
                ['Meet', 'An introductory conversation with our founders.'],
                ['Experience', "You're invited to a GWS event, to see what it's actually like before deciding anything."],
                ['Join', "If it feels right on both sides, you become a member with the membership fee. Your profile goes live on the GWS app, and you're in."],
              ].map(([title, desc], i) => (
                <div
                  key={title}
                  className="rv relative flex gap-5"
                  style={{ '--d': `${0.35 + i * 0.1}s` }}
                >
                  {/* rail  —  the node plus its dotted leg down to the next one */}
                  <div className="flex w-12 shrink-0 flex-col items-center self-stretch">
                    <div className="gws-node" style={{ '--nd': `${i * 0.4}s`, '--hd': `${1 + i * 0.9}s` }}>
                      <div
                        className="gws-node-circle relative flex h-12 w-12 items-center justify-center rounded-full"
                        style={{
                          /* the same graduated fill, now in copper  —  the step reads as filling up */
                          background: `rgba(228, 164, 131, ${[0.3, 0.55, 0.8, 1][i]})`,
                          border: `1px solid ${COPPER}`,
                        }}
                      >
                        <span
                          aria-hidden
                          className="gws-halo absolute inset-0 rounded-full"
                          style={{ border: '1px solid #ffffff' }}
                        />
                        <span
                          className="gws-display text-base"
                          style={{
                            color: GREEN,
                            fontVariationSettings: "'SOFT' 100, 'WONK' 0, 'opsz' 144, 'wght' 700",
                          }}
                        >
                          {i + 1}
                        </span>
                      </div>
                    </div>
                    {i < 3 && (
                      <svg
                        className="my-1.5 w-6 min-h-[36px] flex-1"
                        viewBox="0 0 24 60"
                        preserveAspectRatio="none"
                        fill="none"
                        aria-hidden
                      >
                        <path
                          d={i % 2 === 0 ? 'M 12 4 C 26 20, -2 40, 12 56' : 'M 12 4 C -2 20, 26 40, 12 56'}
                          stroke="rgba(228,164,131,0.5)"
                          /* the run thickens step by step, the way the nodes fill up */
                          strokeWidth={[2, 3, 4][i]}
                          strokeLinecap="round"
                          strokeDasharray="2 9"
                          vectorEffect="non-scaling-stroke"
                        />
                      </svg>
                    )}
                  </div>
                  <div className={`pt-2.5 ${i < 3 ? 'pb-9' : ''}`}>
                    <h3 className="text-lg font-normal text-[#f7f5f1]">{title}</h3>
                    <p className="mt-1 text-base lg:text-[1.25rem] font-light leading-relaxed" style={{ color: '#ffffff' }}>
                      {desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>
      ),
    }]),

    /* 07  —  Channels / End */
    {
      bg: PAPER,
      grow: true,
      content: (
        <div className="relative flex min-h-screen w-full items-center px-8 lg:px-28 pt-[calc(3rem_+_(1400px_-_100vw)_*_0.04_+_11vw_+_0.5rem)] pb-24 lg:py-14">
          <Ghost n="07" text="WHERE TO FIND US" />
          <div className="max-w-6xl w-full mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12 lg:gap-20 items-stretch">
              {[
                ['Website', 'This is where people go to learn about us and apply. It walks through who we are, what we believe, and how the membership process works.', 'https://genwsociety.com'],
                ['LinkedIn', "This is where we share the more professional side of what we do; our Business Table sessions, our speakers and the topics we cover.", 'https://www.linkedin.com/company/generational-wealth-society-gws/'],
                ['Instagram', "Anyone can follow us here. We share the general feel of the community. If you are a member, we also add you to our Close Friends list, where you can see exclusive content.", 'https://www.instagram.com/genwsociety'],
              ].map(([headline, desc, href], i) => (
                <div
                  key={headline}
                  /* dashed copper dividers, pulled halfway into the gap so they sit
                     centred between the columns */
                  className={`rv group flex flex-col ${i > 0 ? 'md:border-l-2 md:border-dashed md:-ml-6 md:pl-6 lg:-ml-10 lg:pl-10' : ''}`}
                  style={{ '--d': `${0.3 + i * 0.12}s`, ...(i > 0 ? { borderColor: COPPER } : {}) }}
                >
                  {/* channel icon above the heading  —  globe / LinkedIn / Instagram */}
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    tabIndex={-1}
                    aria-hidden
                    className="mb-4 flex justify-center"
                  >
                    {i === 0 ? (
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={COPPER} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                    ) : i === 1 ? (
                      <svg width="30" height="30" viewBox="0 0 24 24" fill={COPPER}>
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
                      </svg>
                    ) : (
                      <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke={COPPER} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" />
                        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
                      </svg>
                    )}
                  </a>
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block gws-display uppercase underline underline-offset-4 text-center text-2xl lg:text-3xl transition-colors duration-300"
                    style={{ color: GREEN, fontVariationSettings: "'SOFT' 100, 'WONK' 0, 'opsz' 144, 'wght' 600" }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = COPPER)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = GREEN)}
                  >
                    {headline}
                  </a>
                  <p
                    className="mt-4 flex-1 text-justify text-[17px] lg:text-[20px] font-light leading-[1.75]"
                    style={{ color: 'rgba(26, 68, 31, 0.8)' }}
                  >
                    {desc}
                  </p>
                </div>
              ))}
            </div>

          </div>

          {/* Logo row along the bottom, split in the middle by the contact address */}
          <div
            className="rv absolute bottom-6 left-0 right-0 flex items-center justify-center gap-2 md:gap-5 px-3"
            style={{ '--d': '0.7s' }}
          >
            {[0, 1].map((side) => (
              <React.Fragment key={side}>
                {side === 1 && (
                  <a
                    href="mailto:gws@genwsociety.com"
                    className="shrink-0 text-[13px] sm:text-[16px] lg:text-[18px] font-light tracking-wide transition-colors duration-300"
                    style={{ color: GREEN }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = COPPER)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = GREEN)}
                  >
                    gws@genwsociety.com
                  </a>
                )}
                {/* Below md the 18 marks no longer fit around the address, so the strip
                    stops distributing and just runs off the screen edge instead: it packs
                    against the address and whatever spills past the viewport is clipped. */}
                <div
                  aria-hidden
                  className={`flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden md:gap-0 md:overflow-visible md:justify-between ${
                    side === 0 ? 'justify-end' : 'justify-start'
                  }`}
                >
                  {Array.from({ length: 18 }).map((_, i) => (
                    <div
                      key={i}
                      className="h-3.5 w-3.5 shrink-0 sm:h-4 sm:w-4 lg:h-6 lg:w-6"
                      style={{
                        backgroundColor: [GREEN, COPPER][(i + side) % 2],
                        WebkitMaskImage: `url(${gwsLogo})`,
                        WebkitMaskRepeat: 'no-repeat',
                        WebkitMaskSize: 'contain',
                        WebkitMaskPosition: 'center',
                        maskImage: `url(${gwsLogo})`,
                        maskRepeat: 'no-repeat',
                        maskSize: 'contain',
                        maskPosition: 'center',
                      }}
                    />
                  ))}
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>
      ),
    },
  ];

  return (
    <div id="gws-deck" className="gws-deck relative w-full" style={{ backgroundColor: GREEN_DEEP }}>
      <style>{`
        .gws-deck { font-family: 'Figtree', sans-serif; }
        /* The running band. Two things have to line up for the loop to be seamless:
           a card slot is one card plus one gap, and the track travels exactly --n
           slots  —  the length of one copy of the list  —  before restarting. Both
           percentages resolve against the track's own width, which is pinned to the
           viewport's, so the cards keep the same width as the static rows' columns. */
        .gws-marquee {
          --per: 1;
          --mgap: 0.75rem;
          overflow: hidden;
          min-width: 0;
        }
        @media (min-width: 1024px) {
          .gws-marquee { --per: 3; --mgap: 1rem; }
        }
        .gws-marquee-track {
          display: flex;
          width: 100%;
          gap: var(--mgap);
          animation: gwsMarquee var(--dur) linear infinite;
        }
        .gws-marquee-track > * {
          flex: 0 0 calc((100% - (var(--per) - 1) * var(--mgap)) / var(--per));
          min-width: 0;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        @keyframes gwsMarquee {
          from { transform: translateX(0); }
          to { transform: translateX(calc(-1 * var(--n) * (100% + var(--mgap)) / var(--per))); }
        }
        @media (prefers-reduced-motion: reduce) {
          .gws-marquee-track { animation: none; }
        }
        .gws-display {
          font-family: 'Spirits Soft', 'Figtree', serif;
          font-variation-settings: 'SOFT' 100, 'WONK' 0, 'opsz' 144, 'wght' 380;
          letter-spacing: -0.015em;
        }
        .gws-deck .rv { opacity: 0; }
        .gws-deck .slide-active .rv {
          animation: gwsRise 1.15s cubic-bezier(0.16, 1, 0.3, 1) both;
          animation-delay: var(--d, 0s);
        }
        @keyframes gwsRise {
          from { opacity: 0; transform: translateY(34px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .gws-ghost {
          transform: translateY(0);
        }
        .slide-active .gws-ghost {
          animation: gwsGhostRise 1.2s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes gwsGhostRise {
          from { transform: translateY(80px); opacity: 0; }
          to { transform: translateY(-40px); opacity: 1; }
        }
        .gws-counter {
          transform: translateY(0);
          transition: opacity 0.5s ease;
        }
        .gws-counter-animate {
          animation: gwsCounterRise 1.2s cubic-bezier(0.16, 1, 0.3, 1) both;
        }
        @keyframes gwsCounterRise {
          from { transform: translateY(40px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .gws-blob {
          border-radius: 42% 58% 52% 48% / 46% 42% 58% 54%;
          border: 1px solid rgba(228,164,131,0.25);
          animation: gwsBlob 11s ease-in-out infinite;
        }
        /* Stacked on mobile the balloons lose the layout cues that read them as
           balloons, so the copper outline has to carry it on its own */
        @media (max-width: 1023px) {
          .gws-blob {
            border-width: 2px;
            border-color: rgba(228,164,131,0.55);
          }
        }
        @keyframes gwsBlob {
          0%, 100% { border-radius: 42% 58% 52% 48% / 46% 42% 58% 54%; }
          33% { border-radius: 54% 46% 44% 56% / 42% 54% 46% 58%; }
          66% { border-radius: 46% 54% 58% 42% / 54% 46% 56% 44%; }
        }
        .gws-blob-dot {
          border-radius: 55% 45% 50% 50% / 45% 55% 45% 55%;
          animation: gwsBlobDot 6s ease-in-out infinite;
        }
        @keyframes gwsBlobDot {
          0%, 100% { border-radius: 55% 45% 50% 50% / 45% 55% 45% 55%; transform: translateY(0) scale(1); }
          33% { border-radius: 45% 55% 42% 58% / 58% 45% 55% 45%; transform: translateY(-4px) scale(1.08); }
          66% { border-radius: 50% 50% 58% 42% / 48% 58% 44% 56%; transform: translateY(3px) scale(0.94); }
        }
        .gws-flip { perspective: 1800px; outline: none; }
        .gws-flip-inner {
          position: relative;
          width: 100%;
          height: 100%;
          transform-style: preserve-3d;
          transition: transform 0.9s cubic-bezier(0.2, 0.8, 0.16, 1),
                      box-shadow 0.6s ease;
          border-radius: 18px;
        }
        .gws-flip:hover .gws-flip-inner,
        .gws-flip:focus-visible .gws-flip-inner {
          transform: rotateY(180deg);
          box-shadow: 0 40px 70px -35px rgba(16, 19, 17, 0.45);
        }
        .gws-face {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          border-radius: 18px;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
          transition: border-color 0.5s ease;
        }
        .gws-face-back { transform: rotateY(180deg); }
        .gws-flip-hint { transition: opacity 0.4s ease, letter-spacing 0.4s ease; }
        .gws-flip:hover .gws-flip-hint { opacity: 0; }
        @media (prefers-reduced-motion: reduce) {
          .gws-flip-inner { transition-duration: 0.01ms; }
        }
        .gws-reveal {
          transform: scaleX(0);
          transform-box: fill-box;
          transform-origin: left center;
        }
        .slide-active .gws-reveal {
          animation: gwsReveal 2.4s cubic-bezier(0.65, 0, 0.35, 1) 0.35s forwards;
        }
        @keyframes gwsReveal { to { transform: scaleX(1); } }

        /* Path nodes: a slow drifting wave, plus a halo that travels down the line */
        .slide-active .gws-node {
          animation: gwsFloat 6s ease-in-out infinite;
          animation-delay: var(--nd, 0s);
        }
        @keyframes gwsFloat {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .gws-node-circle {
          transition: transform 0.55s cubic-bezier(0.16, 1, 0.3, 1),
                      box-shadow 0.55s ease, border-color 0.55s ease;
        }
        /* the glow answers the halo, so it carries the same white */
        .group:hover .gws-node-circle {
          transform: scale(1.12);
          border-color: rgba(228, 164, 131, 1);
          box-shadow: 0 0 34px -6px rgba(255, 255, 255, 0.6);
        }
        .gws-halo { opacity: 0; }
        .slide-active .gws-halo {
          animation: gwsHalo 4.5s cubic-bezier(0, 0, 0.2, 1) infinite;
          animation-delay: var(--hd, 0s);
        }
        @keyframes gwsHalo {
          0% { transform: scale(1); opacity: 0.5; }
          60%, 100% { transform: scale(1.75); opacity: 0; }
        }
        @media (prefers-reduced-motion: reduce) {
          .slide-active .gws-node, .slide-active .gws-halo { animation: none; }
        }
        .gws-ping { animation: gwsPing 2.6s cubic-bezier(0, 0, 0.2, 1) infinite; }
        @keyframes gwsPing {
          0% { transform: scale(1); opacity: 0.5; }
          80%, 100% { transform: scale(1.85); opacity: 0; }
        }
        /* The flip-hint arrow keeps redrawing itself: the shaft comes in from the left,
           the head strokes land last (they sit after the shaft in the path, so the dash
           reaches them at the end), it holds a beat, then slips out through the tip. */
        .gws-arrow {
          stroke-dasharray: 57;
          animation: gwsArrow 3s ease-in-out infinite;
        }
        @keyframes gwsArrow {
          0% { stroke-dashoffset: 57; }
          45% { stroke-dashoffset: 0; }
          65% { stroke-dashoffset: 0; }
          95%, 100% { stroke-dashoffset: -57; }
        }
        @media (prefers-reduced-motion: reduce) {
          .gws-arrow { animation: none; stroke-dashoffset: 0; }
        }
        .gws-scrollline { animation: gwsLine 2.2s cubic-bezier(0.76, 0, 0.24, 1) infinite; }
        @keyframes gwsLine {
          0% { transform: scaleY(0); transform-origin: top; }
          45% { transform: scaleY(1); transform-origin: top; }
          55% { transform: scaleY(1); transform-origin: bottom; }
          100% { transform: scaleY(0); transform-origin: bottom; }
        }
      `}</style>

      {/* Sections - Normal Scroll */}
      <div className="w-full">
        {slides.map((slide, i) => (
          <section
            key={i}
            /* Below lg the viewport floor comes off growable slides: each one's own
               inner min-h-screen still holds it up, so only the slides that opt out of
               that floor actually shrink to their content instead of padding out to a
               full screen the reader then has to scroll past. */
            /* -mt-px: section heights land on fractional pixels, and the sliver between
               two of them lets the deck's dark backdrop through as a hairline. Overlap
               them by one pixel so each section's own background covers the seam. */
            className={`relative w-full overflow-hidden slide-active ${i > 0 ? '-mt-px' : ''} ${
              slide.grow ? 'lg:min-h-screen' : 'h-screen'
            }`}
            style={{ background: slide.bg }}
          >
            {slide.content}
          </section>
        ))}
      </div>

      {/* Grain overlay */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 z-40 opacity-[0.035]"
        style={{ backgroundImage: GRAIN, backgroundSize: '180px' }}
      />
    </div>
  );
};

export default PitchDeck;
